import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@noor/api';
import { toast } from '@noor/lib';
import {
  moderationApi,
  type ApproveChangeRequestArgs,
  type ModerationEntityType,
} from '@/entities/moderation';
import { useInvalidateModeration } from './use-invalidate-moderation';

/**
 * Approve mutation for a moderation change request. Invalidate-only (no
 * optimistic patch — the applied payload is server-computed, so predicting
 * it client-side would lie). A failure refreshes too: the usual cause is a
 * request someone else already decided (or a sibling approval withdrew), and
 * the open modal must show that instead of a stale "pending". Surfaces its
 * own toast, so the global MutationCache handler stays quiet for it.
 */
export function useApproveChangeRequest(entityType: ModerationEntityType) {
  const { t } = useTranslation('moderation');
  const invalidate = useInvalidateModeration(entityType);
  return useMutation({
    mutationFn: (args: ApproveChangeRequestArgs) => moderationApi.approve(args),
    onSuccess: () => {
      invalidate();
      toast.success(t('approve.success', 'Изменение одобрено'));
    },
    onError: (error) => {
      invalidate();
      toast.error(getApiErrorMessage(error, t('approve.error', 'Не удалось одобрить')));
    },
  });
}
